import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Bell, FileText, Download, Clock, CheckCircle2,
  AlertCircle, Loader2, RefreshCw, X, ChevronDown, ChevronUp,
  Calendar, Building2, Upload, Image, File, Trash2
} from 'lucide-react';
import { useClientAuth } from '@/context/ClientAuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const statusConfig = {
  todo: { label: 'Te Doen', color: 'bg-slate-500', textColor: 'text-slate-400' },
  in_progress: { label: 'In Uitvoering', color: 'bg-blue-500', textColor: 'text-blue-400' },
  review: { label: 'Review', color: 'bg-yellow-500', textColor: 'text-yellow-400' },
  done: { label: 'Voltooid', color: 'bg-green-500', textColor: 'text-green-400' }
};

const ClientProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isLoading: authLoading } = useClientAuth();
  
  const [project, setProject] = useState(null);
  const [kanban, setKanban] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('kanban');
  const [expandedUpdates, setExpandedUpdates] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    setIsLoading(true);
    try {
      // Fetch project details
      const projectRes = await fetch(`${API_URL}/api/portal/projects/${projectId}`, {
        headers: getAuthHeaders()
      });
      
      if (!projectRes.ok) {
        if (projectRes.status === 403 || projectRes.status === 404) {
          toast.error('Project niet gevonden');
          navigate('/portal/dashboard');
          return;
        }
        throw new Error('Kon project niet laden');
      }
      
      const projectData = await projectRes.json();
      setProject(projectData);

      // Fetch kanban board
      const kanbanRes = await fetch(`${API_URL}/api/portal/projects/${projectId}/kanban`, {
        headers: getAuthHeaders()
      });
      
      if (kanbanRes.ok) {
        const kanbanData = await kanbanRes.json();
        setKanban(kanbanData);
      }
    } catch (error) {
      // Error handled silently
      toast.error('Er ging iets mis');
    } finally {
      setIsLoading(false);
    }
  };

  const markUpdateAsRead = async (updateId) => {
    try {
      await fetch(`${API_URL}/api/portal/projects/${projectId}/updates/${updateId}/read`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      // Update local state
      setProject(prev => ({
        ...prev,
        updates: prev.updates.map(u => 
          u.id === updateId ? { ...u, is_read: true } : u
        ),
        unread_updates: Math.max(0, prev.unread_updates - 1)
      }));
    } catch (error) {
      // Error handled silently
    }
  };

  const markAllAsRead = async () => {
    try {
      await fetch(`${API_URL}/api/portal/projects/${projectId}/updates/read-all`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      setProject(prev => ({
        ...prev,
        updates: prev.updates.map(u => ({ ...u, is_read: true })),
        unread_updates: 0
      }));
      
      toast.success('Alle updates gelezen');
    } catch (error) {
      // Error handled silently
    }
  };

  const downloadFile = async (file) => {
    try {
      const response = await fetch(`${API_URL}${file.download_url}`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) throw new Error('Download mislukt');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.original_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Bestand gedownload');
    } catch (error) {
      // Error handled silently
      toast.error('Download mislukt');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleFileUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          toast.error(`${file.name} is te groot (max 10MB)`);
          continue;
        }

        const formData = new FormData();
        formData.append('file', file);

        const response = await fetch(`${API_URL}/api/portal/projects/${projectId}/files`, {
          method: 'POST',
          headers: {
            'Authorization': getAuthHeaders()['Authorization']
          },
          body: formData
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Upload mislukt');
        }

        setUploadProgress(Math.round(((i + 1) / files.length) * 100));
      }

      toast.success(`${files.length} bestand${files.length > 1 ? 'en' : ''} geüpload`);
      fetchProjectData(); // Refresh to show new files
    } catch (error) {
      // Error handled silently
      toast.error(error.message || 'Upload mislukt');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const getFileIcon = (filename) => {
    const ext = filename?.split('.').pop()?.toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) {
      return <Image className="w-6 h-6 text-green-400" />;
    }
    return <File className="w-6 h-6 text-blue-400" />;
  };

  const getTaskProgress = () => {
    if (!project?.tasks || project.tasks.length === 0) return 0;
    const done = project.tasks.filter(t => t.status === 'done').length;
    return Math.round((done / project.tasks.length) * 100);
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700/50 sticky top-0 z-50 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/portal/dashboard"
                className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-white">{project.name}</h1>
                <p className="text-xs text-slate-400">Project Details</p>
              </div>
            </div>
            
            <button
              onClick={fetchProjectData}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
              title="Vernieuwen"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Project Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 mb-6"
        >
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h2 className="text-xl font-bold text-white mb-2">{project.name}</h2>
              {project.description && (
                <p className="text-slate-400">{project.description}</p>
              )}
            </div>
            
            <div className="flex items-center gap-6">
              {/* Progress */}
              <div className="min-w-[150px]">
                <div className="flex items-center justify-between text-sm mb-2">
                  <span className="text-slate-400">Voortgang</span>
                  <span className="text-white font-medium">{getTaskProgress()}%</span>
                </div>
                <div className="h-3 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-blue-500 to-blue-400 rounded-full transition-all"
                    style={{ width: `${getTaskProgress()}%` }}
                  />
                </div>
              </div>
              
              {/* Stats */}
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{project.tasks?.length || 0}</p>
                  <p className="text-xs text-slate-400">Taken</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{project.files_count || 0}</p>
                  <p className="text-xs text-slate-400">Bestanden</p>
                </div>
                {project.unread_updates > 0 && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-red-400">{project.unread_updates}</p>
                    <p className="text-xs text-slate-400">Ongelezen</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700/50 pb-2">
          {[
            { id: 'kanban', label: 'Kanban Bord', icon: CheckCircle2 },
            { id: 'updates', label: 'Updates', icon: Bell, badge: project.unread_updates },
            { id: 'files', label: 'Bestanden', icon: FileText, badge: project.files_count }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-500/20 text-blue-400'
                  : 'text-slate-400 hover:text-white hover:bg-slate-700/50'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.badge > 0 && (
                <span className="px-1.5 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          {activeTab === 'kanban' && kanban && (
            <motion.div
              key="kanban"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
              data-testid="kanban-board"
            >
              {kanban.columns.map(column => (
                <div
                  key={column.status}
                  className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4"
                >
                  <div className="flex items-center gap-2 mb-4">
                    <div className={`w-3 h-3 rounded-full ${statusConfig[column.status]?.color || 'bg-slate-500'}`} />
                    <h3 className="font-medium text-white">{column.title}</h3>
                    <span className="ml-auto text-sm text-slate-400">{column.tasks.length}</span>
                  </div>
                  
                  <div className="space-y-3 min-h-[200px]">
                    {column.tasks.length === 0 ? (
                      <p className="text-slate-500 text-sm text-center py-8">Geen taken</p>
                    ) : (
                      column.tasks.map(task => (
                        <div
                          key={task.id}
                          className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-3 hover:border-slate-600/50 transition-all"
                        >
                          <p className="text-sm font-medium text-white mb-1">{task.title}</p>
                          {task.description && (
                            <p className="text-xs text-slate-400 line-clamp-2">{task.description}</p>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              ))}
            </motion.div>
          )}

          {activeTab === 'updates' && (
            <motion.div
              key="updates"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              data-testid="updates-list"
            >
              {project.updates?.length > 0 && project.unread_updates > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="mb-4 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                >
                  Markeer alles als gelezen
                </button>
              )}
              
              {!project.updates || project.updates.length === 0 ? (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
                  <Bell className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Nog geen updates</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {project.updates.map(update => (
                    <div
                      key={update.id}
                      className={`bg-slate-800/50 border rounded-xl p-4 transition-all ${
                        !update.is_read 
                          ? 'border-blue-500/30 bg-blue-500/5' 
                          : 'border-slate-700/50'
                      }`}
                      onClick={() => !update.is_read && markUpdateAsRead(update.id)}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            {update.is_important && (
                              <AlertCircle className="w-4 h-4 text-yellow-400" />
                            )}
                            <h4 className="font-medium text-white">{update.title}</h4>
                            {!update.is_read && (
                              <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                                Nieuw
                              </span>
                            )}
                          </div>
                          <p className="text-slate-400 text-sm">{update.message}</p>
                        </div>
                        <p className="text-xs text-slate-500 whitespace-nowrap">
                          {formatDate(update.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {activeTab === 'files' && (
            <motion.div
              key="files"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              data-testid="files-list"
            >
              {/* Upload Section */}
              <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Upload className="w-5 h-5 text-blue-400" />
                  Bestanden uploaden
                </h3>
                <p className="text-slate-400 text-sm mb-4">
                  Upload foto's, documenten of andere bestanden voor je project. Max 10MB per bestand.
                </p>
                
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                
                <label
                  htmlFor="file-upload"
                  className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer transition-all ${
                    uploading 
                      ? 'border-blue-500 bg-blue-500/10' 
                      : 'border-slate-600 hover:border-blue-500 hover:bg-slate-700/30'
                  }`}
                >
                  {uploading ? (
                    <div className="text-center">
                      <Loader2 className="w-8 h-8 text-blue-400 animate-spin mx-auto mb-2" />
                      <p className="text-blue-400 font-medium">Uploaden... {uploadProgress}%</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <p className="text-slate-300 font-medium">Klik om bestanden te selecteren</p>
                      <p className="text-slate-500 text-sm">of sleep ze hierheen</p>
                    </div>
                  )}
                </label>
              </div>

              {/* Files List */}
              {!project.files || project.files.length === 0 ? (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">Nog geen bestanden geüpload</p>
                  <p className="text-slate-500 text-sm mt-2">Upload je eerste bestand hierboven</p>
                </div>
              ) : (
                <div className="grid gap-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <FileText className="w-5 h-5 text-slate-400" />
                    Geüploade bestanden ({project.files.length})
                  </h3>
                  {project.files.map(file => (
                    <div
                      key={file.id}
                      className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 flex items-center justify-between gap-4 hover:border-slate-600/50 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <div className="p-3 bg-slate-700/50 rounded-lg">
                          {getFileIcon(file.original_name)}
                        </div>
                        <div>
                          <p className="font-medium text-white">{file.original_name}</p>
                          <p className="text-sm text-slate-400">
                            {formatFileSize(file.file_size)} • {formatDate(file.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      
                      <button
                        onClick={() => downloadFile(file)}
                        className="p-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition-all"
                        title="Download"
                        data-testid={`download-file-${file.id}`}
                      >
                        <Download className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
};

export default ClientProjectDetail;
